import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { UserProfile, UserRole, AccountStatus } from '@/models/user.model';
import {
  auditStatusChange,
  auditListQuery,
} from '@/utils/audit-integration';
import { AuditLogService } from '@/services/audit-log.service';
import { AuditEntityType, AuditLogAction, AuditLogSeverity } from '@/models/audit-log.model';

const USER_PROFILES_COLLECTION = 'userProfiles';

export class UserManagementService {
  /**
   * Get all user profiles
   */
  static async getAllUsers(): Promise<UserProfile[]> {
    const q = query(
      collection(db, USER_PROFILES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => this.docToUserProfile(doc.id, doc.data()));

    auditListQuery(AuditEntityType.user, {
      queryName: 'all_users',
      resultCount: users.length,
      containsPHI: true,
    }).catch(console.error);

    return users;
  }

  /**
   * Get user profile by ID
   */
  static async getUserById(userId: string): Promise<UserProfile | null> {
    const docRef = doc(db, USER_PROFILES_COLLECTION, userId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToUserProfile(docSnap.id, docSnap.data());
  }

  /**
   * Subscribe to user profiles changes
   */
  static subscribeToUsers(
    callback: (users: UserProfile[]) => void
  ): () => void {
    const q = query(
      collection(db, USER_PROFILES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const users = snapshot.docs.map(doc =>
        this.docToUserProfile(doc.id, doc.data())
      );
      callback(users);
    });
  }

  /**
   * Filter users by role
   */
  static async filterUsersByRole(role: UserRole): Promise<UserProfile[]> {
    const q = query(
      collection(db, USER_PROFILES_COLLECTION),
      where('role', '==', role),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => this.docToUserProfile(doc.id, doc.data()));

    auditListQuery(AuditEntityType.user, {
      queryName: 'filter_users_by_role',
      resultCount: users.length,
      filters: { role },
      containsPHI: true,
    }).catch(console.error);

    return users;
  }

  /**
   * Filter users by status
   */
  static async filterUsersByStatus(status: AccountStatus): Promise<UserProfile[]> {
    const q = query(
      collection(db, USER_PROFILES_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => this.docToUserProfile(doc.id, doc.data()));

    auditListQuery(AuditEntityType.user, {
      queryName: 'filter_users_by_status',
      resultCount: users.length,
      filters: { status },
      containsPHI: true,
    }).catch(console.error);

    return users;
  }

  /**
   * Search users
   */
  static async searchUsers(searchTerm: string): Promise<UserProfile[]> {
    const allUsers = await this.getAllUsers();
    const lowerSearch = searchTerm.toLowerCase();
    
    const filtered = allUsers.filter(user =>
      user.email.toLowerCase().includes(lowerSearch) ||
      user.firstName?.toLowerCase().includes(lowerSearch) ||
      user.lastName?.toLowerCase().includes(lowerSearch) ||
      user.displayName?.toLowerCase().includes(lowerSearch)
    );

    auditListQuery(AuditEntityType.user, {
      queryName: 'search_users',
      resultCount: filtered.length,
      filters: { searchTerm },
      containsPHI: true,
    }).catch(console.error);

    return filtered;
  }

  /**
   * Update user profile
   */
  static async updateUser(
    userId: string,
    updates: Partial<UserProfile>
  ): Promise<void> {
    const docRef = doc(db, USER_PROFILES_COLLECTION, userId);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.displayName !== undefined) updateData.displayName = updates.displayName;
    if (updates.firstName !== undefined) updateData.firstName = updates.firstName;
    if (updates.lastName !== undefined) updateData.lastName = updates.lastName;
    if (updates.phoneNumber !== undefined) updateData.phoneNumber = updates.phoneNumber;
    if (updates.role !== undefined) updateData.role = updates.role;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.profileImageUrl !== undefined) updateData.profileImageUrl = updates.profileImageUrl;
    if (updates.dateOfBirth !== undefined) {
      updateData.dateOfBirth = updates.dateOfBirth
        ? Timestamp.fromDate(updates.dateOfBirth)
        : null;
    }
    if (updates.gender !== undefined) updateData.gender = updates.gender;
    if (updates.address !== undefined) updateData.address = updates.address;
    if (updates.city !== undefined) updateData.city = updates.city;
    if (updates.state !== undefined) updateData.state = updates.state;
    if (updates.zipCode !== undefined) updateData.zipCode = updates.zipCode;
    if (updates.emergencyContactName !== undefined) updateData.emergencyContactName = updates.emergencyContactName;
    if (updates.emergencyContactPhone !== undefined) updateData.emergencyContactPhone = updates.emergencyContactPhone;
    if (updates.mustChangePassword !== undefined) updateData.mustChangePassword = updates.mustChangePassword;

    await updateDoc(docRef, updateData);
  }

  /**
   * Update user status
   */
  static async updateUserStatus(
    userId: string,
    status: AccountStatus
  ): Promise<void> {
    const user = await this.getUserById(userId);
    const oldStatus = user?.status;
    
    await this.updateUser(userId, { status });
    
    // Log status change
    auditStatusChange(
      AuditEntityType.user,
      userId,
      user?.email,
      String(oldStatus),
      String(status)
    ).catch(console.error);
  }

  /**
   * Update user role
   */
  static async updateUserRole(
    userId: string,
    role: UserRole
  ): Promise<void> {
    const user = await this.getUserById(userId);
    const oldRole = user?.role;
    
    await this.updateUser(userId, { role });
    
    // Log role change - this is a security-sensitive operation
    AuditLogService.writeLog({
      action: AuditLogAction.userRoleChanged,
      severity: AuditLogSeverity.warning,
      description: `User role changed: ${user?.email} (${UserRole[oldRole || 0]} → ${UserRole[role]})`,
      entityType: AuditEntityType.user,
      entityId: userId,
      entityName: user?.email,
      changeDetails: {
        changeType: 'update',
        fieldsChanged: ['role'],
        beforeValues: { role: oldRole },
        afterValues: { role: role },
      },
      success: true,
    }).catch(console.error);
  }

  /**
   * Convert Firestore document to UserProfile
   */
  private static docToUserProfile(userId: string, data: any): UserProfile {
    return {
      userId,
      email: data.email || '',
      displayName: data.displayName,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      role: data.role !== undefined ? data.role : UserRole.member,
      status: data.status !== undefined ? data.status : AccountStatus.pendingVerification,
      profileImageUrl: data.profileImageUrl,
      dateOfBirth: data.dateOfBirth?.toDate(),
      gender: data.gender,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      emergencyContactName: data.emergencyContactName,
      emergencyContactPhone: data.emergencyContactPhone,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
      metadata: data.metadata,
      mustChangePassword: data.mustChangePassword ?? false,
    };
  }
}

